import type { SubsPlanType } from '@/app/types';
import { createI18nConfig } from '@/app/utils';
import type { CreditV1OrderInfo, FaqItem } from './types';

export const PAGE_SIZE = 10;

export const PAY_CHANNEL_MAP: Record<CreditV1OrderInfo['pay_channel'], string> = {
  unspecified: '未知',
  alipay: '支付宝',
  stripe: 'Stripe',
  google: 'Google Pay',
  apple: 'Apple Pay',
};

export const PLAN_TYPE_MAP: Record<SubsPlanType, string> = {
  free: 'text-system-white',
  standard_v2: 'bg-[linear-gradient(90deg,#CFE7FF_0%,#79A4FF_100%)] text-transparent',
  premium_v2: 'bg-[linear-gradient(91deg,#FAF1C8_3.96%,#BDA16C_96.78%)] text-transparent',
  ultimate_v2: 'bg-[linear-gradient(91deg,#E0DDFF_2.78%,#B494F2_95.83%)] text-transparent',
  team_v2: 'text-system-white',
};

export const PRICING_FAQ = createI18nConfig<FaqItem[]>(
  {
    en: [
      {
        id: 'question_credits',
        question: 'How to Subscribe?',
        children: [
          {
            question: 'What are credits?',
            answer:
              'Credits are used to generate videos and come in three types:\n1. Subscription credits: the credit allowance included in your plan, valid for 30 days.\n2. Purchased credits: extra credits you can buy as needed, valid for 2 years.\n3. Bonus credits: extra credit rewards earned through events, competitions, the Artist Program, and similar activities, valid for 2 years.',
          },
          {
            question: 'How to get credits?',
            answer:
              '1. Sign-up bonus: New users receive trial credits after registering for Vidu.\n2. Subscription: Subscribe to a plan to get more credits.\n3. Daily login: Log in every day to earn credits.\n4. Events: Take part in official events, competitions, or the Artist Program to earn bonus credits. See the Vidu Events section for details.',
          },
          {
            question: 'How are the credit costs for different tasks determined?',
            answer:
              'The following are the credit costs for a single video.\nReference-to-video:\nVidu Q3: Flash (1080p) 1s-16s, 3 credits/s; Cinematic (1080p) 1s-16s, 6 credits/s.\nVidu Q2 Pro: 1080p, 2s-5s costs 20 credits, 6s-8s costs 30 credits.\nVidu Q2: 1080p, 2s-5s costs 12 credits, 6s-8s costs 20 credits.\nVidu Q1: 1080p, 5s costs 20 credits.\nVidu 2.0: Speed 4s costs 4 credits; 720p 4s costs 12 credits.\nImage-to-video:\nVidu Q3: Flash (1080p) 1s-5s costs 10 credits, 6s-10s costs 30 credits, 11s-14s costs 50 credits, 15s-16s costs 70 credits; Cinematic (1080p) 1s-5s costs 50 credits, 6s-10s costs 90 credits, 11s-14s costs 130 credits, 15s-16s costs 150 credits.\nVidu Q2: Flash (1080p) 2s-5s costs 10 credits, 6s-8s costs 20 credits; Cinematic (1080p) 2s-5s costs 35 credits, 6s-8s costs 70 credits.\nVidu Q1: 1080p, 5s costs 20 credits.\nVidu 2.0: Speed 4s costs 4 credits; 720p 4s costs 12 credits, 8s costs 24 credits; 1080p 4s costs 24 credits.\nText-to-video:\nVidu Q3: Flash (1080p) 1s-5s costs 10 credits, 6s-10s costs 25 credits, 11s-14s costs 40 credits, 15s-16s costs 60 credits; Cinematic (1080p) 1s-5s costs 40 credits, 6s-10s costs 80 credits, 11s-14s costs 120 credits, 15s-16s costs 140 credits.\nVidu Q2: 1080p, 2s-5s costs 8 credits, 6s-8s costs 15 credits.\nVidu Q1: 1080p, 5s costs 20 credits.\nIf you generate 4 videos at once, total credits = single-run credits × 4.',
          },
          {
            question: 'Do unused subscription credits roll over to the next subscription cycle?',
            answer:
              'No. Unused subscription credits do not roll over to the next cycle. We recommend using them within your current subscription period.',
          },
          {
            question: 'What can I do if I run out of credits?',
            answer:
              'We recommend choosing a subscription plan that better suits your needs. If you still need more credits, you can purchase additional credits. After logging in, open "Plan Details" in the top-right corner of the page and click "Buy Credits" to select a credit package and complete the payment.',
          },
          {
            question: 'Will credits be refunded if my video generation fails?',
            answer:
              'There are usually two reasons a video generation fails:\n1. The generation failed due to high server load, network issues, or system maintenance or updates.\n2. The video did not pass content moderation due to a violation.\nIn the first case, the system will automatically refund the credits used for that task.',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: 'Subscription Plans',
        children: [
          {
            question: 'What are the differences between the different subscription plans?',
            answer:
              'Each subscription plan offers different amounts of credits, different task priorities, and different access to additional features. Visit the "Subscription Plans" section on our website for a detailed comparison of the plans.',
          },
          {
            question: 'Can I subscribe for one month only?',
            answer:
              'We currently offer only monthly and annual subscriptions. If you wish to subscribe for just one month, you can cancel anytime before the next billing cycle.',
          },
          {
            question: 'Can I share my subscription with others?',
            answer:
              'Currently, subscriptions are tied to individual accounts and cannot be shared. Each user must have their own plan.',
          },
          {
            question: 'How to change my subscription plan?',
            answer:
              "Unfortunately, we don't support direct changes to subscription plans. However, you can cancel your current plan and re-subscribe to your preferred plan. The new plan will take effect in the next subscription cycle. Direct plan changes will be available soon.",
          },
          {
            question: 'Will I be automatically charged? How to cancel auto-renewal?',
            answer:
              'Yes, you will be charged automatically at the beginning of each billing cycle. To cancel auto-renewal, go to your account settings and cancel your current subscription plan.',
          },
        ],
      },
      {
        id: 'question_payments',
        question: 'Payments',
        children: [
          {
            question: 'What payment methods does Vidu accept?',
            answer:
              'We currently only support payments through our payment partner Stripe. You can use credit or debit cards (Visa, MasterCard, UnionPay, etc.) to complete the payments. We will provide more payment options in the future.',
          },
          {
            question: 'How to request an invoice?',
            answer:
              'An invoice will be automatically sent to the email address provided at the time of payment. If you do not receive it, please check your spam or junk folder.',
          },
          {
            question: 'Why did my payment fail?',
            answer:
              'Payment failures can occur due to issues with your payment method, insufficient funds, or technical problems. Please ensure that your payment details are up-to-date and try again. If the issue persists, please contact support@vidu.com for further assistance.',
          },
          {
            question: 'Can I request a refund?',
            answer:
              'We’re sorry, but refunds are not supported for the moment. You can cancel your subscription plan before the next billing cycle to avoid future charges.',
          },
          {
            question: 'How to change my payment method?',
            answer:
              'To modify your payment method, click on your profile avatar, go to the "Subscribe" page from the dropdown menu, and click "Modify Payment Method" to be redirected to Stripe for changes.',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    zh: [
      {
        id: 'question_credits',
        question: '积分',
        children: [
          {
            question: '什么是积分？',
            answer:
              '积分可用于生成视频，分为三种类型：\n1. 订阅积分：套餐内包含的积分额度，有效期 30 天。\n2. 购买积分：可按需额外购买的积分，有效期 2 年。\n3. 奖励积分：通过活动、比赛、艺术家计划等方式获得的额外积分奖励，有效期 2 年。',
          },
          {
            question: '如何获取积分？',
            answer:
              '1. 注册赠送积分：新用户注册 Vidu 后，可获得体验积分。\n2. 订阅获取积分：可通过订阅服务获得更多积分。\n3. 每日登录获取积分：每日登录可获得积分。\n4. 参与活动获取积分：参与官方活动、比赛或艺术家计划，可获得额外积分奖励，详情可查看 Vidu 活动专区。',
          },
          {
            question: '如何确定不同任务的积分消耗明细？',
            answer:
              '以下为单个视频的积分消耗标准。\n参考生视频：\nVidu Q3：闪电出片（1080p）1s-16s，3 积分/s；电影大片（1080p）1s-16s，6 积分/s。\nVidu Q2 Pro：1080p，2s-5s 消耗 20 积分，6s-8s 消耗 30 积分。\nVidu Q2：1080p，2s-5s 消耗 12 积分，6s-8s 消耗 20 积分。\nVidu Q1：1080p，5s 消耗 20 积分。\nVidu 2.0：极速 4s 消耗 4 积分；720p 4s 消耗 12 积分。\n图生视频：\nVidu Q3：闪电出片（1080p）1s-5s 消耗 10 积分，6s-10s 消耗 30 积分，11s-14s 消耗 50 积分，15s-16s 消耗 70 积分；电影大片（1080p）1s-5s 消耗 50 积分，6s-10s 消耗 90 积分，11s-14s 消耗 130 积分，15s-16s 消耗 150 积分。\nVidu Q2：闪电出片（1080p）2s-5s 消耗 10 积分，6s-8s 消耗 20 积分；电影大片（1080p）2s-5s 消耗 35 积分，6s-8s 消耗 70 积分。\nVidu Q1：1080p，5s 消耗 20 积分。\nVidu 2.0：极速 4s 消耗 4 积分；720p 4s 消耗 12 积分、8s 消耗 24 积分；1080p 4s 消耗 24 积分。\n文生视频：\nVidu Q3：闪电出片（1080p）1s-5s 消耗 10 积分，6s-10s 消耗 25 积分，11s-14s 消耗 40 积分，15s-16s 消耗 60 积分；电影大片（1080p）1s-5s 消耗 40 积分，6s-10s 消耗 80 积分，11s-14s 消耗 120 积分，15s-16s 消耗 140 积分。\nVidu Q2：1080p，2s-5s 消耗 8 积分，6s-8s 消耗 15 积分。\nVidu Q1：1080p，5s 消耗 20 积分。\n如一次生成 4 个视频，总消耗积分 = 单次积分 × 4。',
          },
          {
            question: '未使用的订阅积分会转入下一个订阅周期吗？',
            answer: '不会。未使用的订阅积分不会转入下一个周期，建议您在当前订阅有效期内及时使用。',
          },
          {
            question: '如果我的积分用完了，该怎么办？',
            answer:
              '建议您选择更适合自己使用需求的订阅计划。如果积分仍然不足，也可以购买额外积分。登录后，在页面右上角进入“套餐详情”，点击“购买积分”即可选择对应积分套餐并完成支付。',
          },
          {
            question: '如果我的视频生成失败，是否会退还积分？',
            answer:
              '视频生成失败通常有两种情况：\n1. 由于服务器负载高、网络问题、系统维护或更新导致生成失败。\n2. 视频因违规内容审核不通过。\n如果属于第 1 种情况，系统将自动退还该任务消耗的积分。',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: '订阅套餐',
        children: [
          {
            question: '不同订阅方案之间有何区别？',
            answer:
              '每种订阅方案在积分数量、任务优先级以及功能访问权限方面有所不同。您可以访问我们官网的“订阅方案”页面，查看详细对比。',
          },
          {
            question: '可以只订阅一个月吗？',
            answer: '我们目前仅提供月度和年度订阅。如您仅希望订阅一个月，可在下一个计费周期前随时取消订阅。',
          },
          {
            question: '可以将我的订阅与他人共享吗？',
            answer: '目前订阅仅限于个人账户，无法共享。每位用户都需单独订阅。',
          },
          {
            question: '如何更换订阅方案？',
            answer:
              '目前暂不支持直接更换订阅方案。但您可以先取消当前方案，再重新订阅您想要的方案。新方案将在下一个计费周期生效。我们将很快支持直接更改方案功能，敬请期待。',
          },
          {
            question: '是否会自动扣费？如何取消自动续费？',
            answer: '是的，我们将在每个计费周期开始时自动扣费。如需取消自动续费，请前往账户设置，取消当前订阅方案。',
          },
        ],
      },
      {
        id: 'question_payments',
        question: '付款问题',
        children: [
          {
            question: 'Vidu 支持哪些支付方式？',
            answer:
              '我们目前仅支持通过支付合作伙伴 Stripe 完成付款。您可以使用信用卡或借记卡（Visa、MasterCard、银联等）支付。我们未来将提供更多支付选项。',
          },
          {
            question: '如何申请发票？',
            answer: '发票将在付款时填写的邮箱地址中自动发送。如未收到，请检查垃圾邮箱或广告邮件文件夹。',
          },
          {
            question: '为什么我的付款失败了？',
            answer:
              '付款失败可能是由于支付方式问题、账户余额不足或技术故障导致。请确保支付信息正确并重新尝试。如问题仍未解决，请联系 support@vidu.com 获取帮助。',
          },
          {
            question: '可以申请退款吗？',
            answer: '很抱歉，目前我们暂不支持退款。您可以在下一个计费周期开始前取消订阅计划，以避免后续扣款。',
          },
          {
            question: '如何更改支付方式？',
            answer:
              '如需更改支付方式，请点击头像，在下拉菜单中进入“订阅”页面，点击“修改支付方式”，系统会跳转至 Stripe 页面进行操作。',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    ja: [
      {
        id: 'question_credits',
        question: 'クレジットについて',
        children: [
          {
            question: 'クレジットとは？',
            answer:
              'クレジットは動画生成に使用でき、3種類があります：\n1. サブスクリプションクレジット：プランに含まれるクレジット枠で、有効期間は30日間です。\n2. 購入クレジット：必要に応じて追加購入できるクレジットで、有効期間は2年間です。\n3. ボーナスクレジット：イベント、コンテスト、アーティストプログラムなどで獲得できる追加クレジットで、有効期間は2年間です。',
          },
          {
            question: 'クレジットの取得方法は？',
            answer:
              '1. 登録ボーナス：Viduに新規登録すると、体験用クレジットがもらえます。\n2. サブスクリプション：プランに加入するとより多くのクレジットを獲得できます。\n3. 毎日ログイン：毎日ログインするとクレジットを獲得できます。\n4. イベント参加：公式イベント、コンテスト、アーティストプログラムに参加するとボーナスクレジットを獲得できます。詳細はViduイベントセクションをご確認ください。',
          },
          {
            question: 'タスクごとのクレジット消費はどのように決まりますか？',
            answer:
              '以下は動画1本あたりのクレジット消費の目安です。\n参照動画生成：\nVidu Q3：フラッシュ（1080p）1s-16s、3クレジット/s；シネマティック（1080p）1s-16s、6クレジット/s。\nVidu Q2 Pro：1080p、2s-5sで20クレジット、6s-8sで30クレジット。\nVidu Q2：1080p、2s-5sで12クレジット、6s-8sで20クレジット。\nVidu Q1：1080p、5sで20クレジット。\nVidu 2.0：速度優先 4sで4クレジット；720p 4sで12クレジット。\n画像から動画生成：\nVidu Q3：フラッシュ（1080p）1s-5sで10クレジット、6s-10sで30クレジット、11s-14sで50クレジット、15s-16sで70クレジット；シネマティック（1080p）1s-5sで50クレジット、6s-10sで90クレジット、11s-14sで130クレジット、15s-16sで150クレジット。\nVidu Q2：フラッシュ（1080p）2s-5sで10クレジット、6s-8sで20クレジット；シネマティック（1080p）2s-5sで35クレジット、6s-8sで70クレジット。\nVidu Q1：1080p、5sで20クレジット。\nVidu 2.0：速度優先 4sで4クレジット；720p 4sで12クレジット、8sで24クレジット；1080p 4sで24クレジット。\nテキストから動画生成：\nVidu Q3：フラッシュ（1080p）1s-5sで10クレジット、6s-10sで25クレジット、11s-14sで40クレジット、15s-16sで60クレジット；シネマティック（1080p）1s-5sで40クレジット、6s-10sで80クレジット、11s-14sで120クレジット、15s-16sで140クレジット。\nVidu Q2：1080p、2s-5sで8クレジット、6s-8sで15クレジット。\nVidu Q1：1080p、5sで20クレジット。\n一度に4本生成する場合、合計クレジット＝1回あたりのクレジット×4となります。',
          },
          {
            question: '未使用のサブスクリプションクレジットは次の請求サイクルに繰り越されますか？',
            answer:
              'いいえ。未使用のサブスクリプションクレジットは次のサイクルに繰り越されません。現在のサブスクリプション有効期間内にご利用ください。',
          },
          {
            question: 'クレジットが足りなくなったら？',
            answer:
              'ご利用に合ったサブスクリプションプランを選ぶことをおすすめします。それでもクレジットが足りない場合は、追加クレジットを購入できます。ログイン後、画面右上の「プラン詳細」を開き、「クレジット購入」をクリックして、ご希望のクレジットパッケージを選択してお支払いください。',
          },
          {
            question: '動画の生成が失敗した場合、クレジットは返還されますか？',
            answer:
              '動画生成の失敗には通常2つの原因があります：\n1. サーバーの高負荷、ネットワークの問題、システムのメンテナンスや更新による失敗。\n2. 違反コンテンツとしてコンテンツ審査を通過しなかった場合。\n1の場合、システムが該当タスクで使用したクレジットを自動で返還します。',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: 'サブスクリプションプランについて',
        children: [
          {
            question: '異なるサブスクリプションプランの違いは何ですか？',
            answer:
              '各プランは、付与されるクレジット、タスクの優先度、利用できる機能に違いがあります。詳細は公式サイトの「サブスクリプションプラン」ページでご確認いただけます。',
          },
          {
            question: '1か月だけの契約はできますか？',
            answer:
              '現在、月額プランと年額プランをご用意しています。1か月のみ利用したい場合は、次の請求サイクル前にいつでも解約可能です。',
          },
          {
            question: '他人とサブスクリプションを共有できますか？',
            answer:
              'サブスクリプションは個人アカウント専用です。共有はできません。すべてのユーザーは個別に契約する必要があります。',
          },
          {
            question: 'サブスクリプションプランの変更はできますか？',
            answer:
              '現時点では直接プランを切り替えることはできません。変更したい場合は、まず現在のプランを解約し、希望のプランに再契約してください。新しいプランは次の請求サイクルから適用されます。今後、直接切り替え可能な機能もサポート予定ですので、ご期待ください。',
          },
          {
            question: '自動更新はされますか？自動更新を停止するには？',
            answer:
              'はい。各請求サイクル開始時に自動で課金されます。自動更新を停止したい場合は、アカウント設定から現在のサブスクリプションを解約してください。',
          },
        ],
      },
      {
        id: 'question_payments',
        question: 'お支払いに関する質問',
        children: [
          {
            question: 'Viduではどのような支払い方法に対応していますか？',
            answer:
              '現在、Viduでは Stripe を通じたお支払いのみ対応しています。クレジットカードやデビットカード（Visa、MasterCard、銀聯など）がご利用可能です。今後、さらに多くの支払い方法にも対応予定です。',
          },
          {
            question: '請求書はどうやって発行できますか？',
            answer:
              '請求書は、お支払い時に入力いただいたメールアドレスに自動で送信されます。届かない場合は、迷惑メールフォルダやプロモーションフォルダもご確認ください。',
          },
          {
            question: 'なぜ支払いが失敗したのでしょうか？',
            answer:
              '支払いの失敗は、支払い方法の問題、口座残高不足、または技術的な不具合などが原因で発生することがあります。支払い情報を再度ご確認のうえ、再度お試しください。それでも解決しない場合は、 support@vidu.com までご連絡ください。',
          },
          {
            question: '返金は可能ですか？',
            answer:
              '申し訳ございませんが、現在は返金には対応しておりません。次回の請求サイクル開始前にサブスクリプションを解約することで、以降の課金を回避できます。',
          },
          {
            question: '支払い方法を変更できますか？',
            answer:
              '支払い方法を変更する場合は、画面右上のアバターをクリックし、ドロップダウンメニューの「サブスクリプション」ページから「支払い方法を変更」を選択してください。その後、Stripeの画面に移動して手続きを行えます。',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    es: [
      {
        id: 'question_credits',
        question: 'Créditos',
        children: [
          {
            question: '¿Qué son los créditos?',
            answer:
              'Los créditos se utilizan para generar vídeos y hay tres tipos:\n1. Créditos de suscripción: la cantidad de créditos incluida en tu plan, válidos durante 30 días.\n2. Créditos comprados: créditos adicionales que puedes comprar según necesites, válidos durante 2 años.\n3. Créditos de bonificación: créditos extra obtenidos a través de eventos, concursos, el Programa de Artistas y actividades similares, válidos durante 2 años.',
          },
          {
            question: '¿Cómo obtener créditos?',
            answer:
              '1. Bonificación por registro: los nuevos usuarios reciben créditos de prueba tras registrarse en Vidu.\n2. Suscripción: suscríbete a un plan para obtener más créditos.\n3. Inicio de sesión diario: obtén créditos iniciando sesión cada día.\n4. Eventos: participa en eventos oficiales, concursos o el Programa de Artistas para obtener créditos de bonificación. Consulta la sección de Eventos de Vidu para más detalles.',
          },
          {
            question: '¿Cómo se determina el consumo de créditos de cada tarea?',
            answer:
              'A continuación se indica el consumo de créditos para un solo vídeo.\nReferencia a vídeo:\nVidu Q3: Flash (1080p) 1s-16s, 3 créditos/s; Cinematográfico (1080p) 1s-16s, 6 créditos/s.\nVidu Q2 Pro: 1080p, 2s-5s cuesta 20 créditos, 6s-8s cuesta 30 créditos.\nVidu Q2: 1080p, 2s-5s cuesta 12 créditos, 6s-8s cuesta 20 créditos.\nVidu Q1: 1080p, 5s cuesta 20 créditos.\nVidu 2.0: Velocidad 4s cuesta 4 créditos; 720p 4s cuesta 12 créditos.\nImagen a vídeo:\nVidu Q3: Flash (1080p) 1s-5s cuesta 10 créditos, 6s-10s cuesta 30 créditos, 11s-14s cuesta 50 créditos, 15s-16s cuesta 70 créditos; Cinematográfico (1080p) 1s-5s cuesta 50 créditos, 6s-10s cuesta 90 créditos, 11s-14s cuesta 130 créditos, 15s-16s cuesta 150 créditos.\nVidu Q2: Flash (1080p) 2s-5s cuesta 10 créditos, 6s-8s cuesta 20 créditos; Cinematográfico (1080p) 2s-5s cuesta 35 créditos, 6s-8s cuesta 70 créditos.\nVidu Q1: 1080p, 5s cuesta 20 créditos.\nVidu 2.0: Velocidad 4s cuesta 4 créditos; 720p 4s cuesta 12 créditos, 8s cuesta 24 créditos; 1080p 4s cuesta 24 créditos.\nTexto a vídeo:\nVidu Q3: Flash (1080p) 1s-5s cuesta 10 créditos, 6s-10s cuesta 25 créditos, 11s-14s cuesta 40 créditos, 15s-16s cuesta 60 créditos; Cinematográfico (1080p) 1s-5s cuesta 40 créditos, 6s-10s cuesta 80 créditos, 11s-14s cuesta 120 créditos, 15s-16s cuesta 140 créditos.\nVidu Q2: 1080p, 2s-5s cuesta 8 créditos, 6s-8s cuesta 15 créditos.\nVidu Q1: 1080p, 5s cuesta 20 créditos.\nSi generas 4 vídeos a la vez, el total de créditos = créditos de una generación × 4.',
          },
          {
            question: '¿Los créditos de suscripción no utilizados pasan al siguiente ciclo de suscripción?',
            answer:
              'No. Los créditos de suscripción no utilizados no pasan al siguiente ciclo. Te recomendamos usarlos dentro de tu período de suscripción actual.',
          },
          {
            question: '¿Qué pasa si me quedo sin créditos?',
            answer:
              'Te recomendamos elegir un plan de suscripción que se ajuste mejor a tus necesidades. Si aún necesitas más créditos, puedes comprar créditos adicionales. Tras iniciar sesión, abre "Detalles del plan" en la esquina superior derecha de la página y haz clic en "Comprar créditos" para seleccionar un paquete de créditos y completar el pago.',
          },
          {
            question: '¿Se reembolsarán los créditos si la generación de mi vídeo falla?',
            answer:
              'Normalmente hay dos motivos por los que falla la generación de un vídeo:\n1. La generación falló por una alta carga del servidor, problemas de red o tareas de mantenimiento o actualización del sistema.\n2. El vídeo no superó la moderación de contenido por una infracción.\nEn el primer caso, el sistema reembolsará automáticamente los créditos utilizados en esa tarea.',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: 'Planes de Suscripción',
        children: [
          {
            question: '¿Cuáles son las diferencias entre los diferentes planes de suscripción?',
            answer:
              'Cada plan de suscripción ofrece diferentes cantidades de créditos, prioridades en tareas y accesos a funciones adicionales. Visite la sección "Planes de Suscripción" en nuestro sitio web para una comparación detallada de los planes.',
          },
          {
            question: '¿Puedo suscribirme solo por un mes?',
            answer:
              'Actualmente, solo ofrecemos suscripciones mensuales y anuales. Si desea suscribirse solo por un mes, puede cancelar en cualquier momento antes del próximo ciclo de facturación.',
          },
          {
            question: '¿Puedo compartir mi suscripción con otras personas?',
            answer:
              'Actualmente, las suscripciones están vinculadas a cuentas individuales y no se pueden compartir. Cada usuario debe tener su propio plan.',
          },
          {
            question: '¿Cómo puedo cambiar mi plan de suscripción?',
            answer:
              'Lamentablemente, no admitimos cambios directos en los planes de suscripción. Sin embargo, puede cancelar su plan actual y volver a suscribirse al plan de su preferencia. El nuevo plan tomará efecto en el próximo ciclo de suscripción. Pronto estarán disponibles los cambios directos de planes.',
          },
          {
            question: '¿Se me cobrará automáticamente? ¿Cómo cancelo la renovación automática?',
            answer:
              'Sí, se le cobrará automáticamente al inicio de cada ciclo de facturación. Para cancelar la renovación automática, vaya a la configuración de su cuenta y cancele su plan de suscripción actual.',
          },
        ],
      },
      {
        id: 'question_payments',
        question: 'Pagos',
        children: [
          {
            question: '¿Qué métodos de pago acepta Vidu?',
            answer:
              'Actualmente, solo admitimos pagos a través de nuestro socio Stripe. Puede utilizar tarjetas de crédito o débito (Visa, MasterCard, UnionPay, etc.) para completar los pagos. Ofreceremos más opciones de pago en el futuro.',
          },
          {
            question: '¿Cómo solicitar una factura?',
            answer:
              'Una factura se enviará automáticamente a la dirección de correo electrónico proporcionada a la hora del pago. Si no la recibe, por favor consulte su carpeta de spam o correo basura.',
          },
          {
            question: '¿Por qué falló mi pago?',
            answer:
              'Las fallas de pago pueden deberse a problemas con su método de pago, fondos insuficientes o problemas técnicos. Por favor asegúrese de que los detalles de su pago estén actualizados e inténtelo nuevamente. Si el problema persiste, por favor contáctenos en support@vidu.com para obtener más asistencia.',
          },
          {
            question: '¿Puedo solicitar un reembolso?',
            answer:
              'Lo sentimos, pero por el momento no se admite el reembolso. Puede cancelar su plan de suscripción antes del próximo ciclo de facturación para evitar cargos futuros.',
          },
          {
            question: '¿Cómo cambiar mi método de pago?',
            answer:
              'Para modificar su método de pago, haga clic en el avatar de su perfil, vaya a la página "Suscribirse" desde el menú desplegable y haga clic en "Modificar Método de Pago" para ser redirigido a Stripe y realizar los cambios.',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    ko: [
      {
        id: 'question_credits',
        question: '크레딧',
        children: [
          {
            question: '크레딧이란 무엇인가요?',
            answer:
              '크레딧은 동영상 생성에 사용되며 세 가지 유형이 있습니다:\n1. 구독 크레딧: 플랜에 포함된 크레딧 한도로, 유효 기간은 30일입니다.\n2. 구매 크레딧: 필요에 따라 추가 구매할 수 있는 크레딧으로, 유효 기간은 2년입니다.\n3. 보너스 크레딧: 이벤트, 대회, 아티스트 프로그램 등을 통해 획득한 추가 크레딧으로, 유효 기간은 2년입니다.',
          },
          {
            question: '크레딧을 어떻게 얻나요?',
            answer:
              '1. 가입 보너스: Vidu에 신규 가입하면 체험 크레딧을 받을 수 있습니다.\n2. 구독: 플랜을 구독하면 더 많은 크레딧을 받을 수 있습니다.\n3. 매일 로그인: 매일 로그인하면 크레딧을 받을 수 있습니다.\n4. 이벤트 참여: 공식 이벤트, 대회 또는 아티스트 프로그램에 참여하면 보너스 크레딧을 받을 수 있습니다. 자세한 내용은 Vidu 이벤트 섹션을 확인하세요.',
          },
          {
            question: '작업별 크레딧 소모량은 어떻게 결정되나요?',
            answer:
              '다음은 동영상 1개당 크레딧 소모 기준입니다.\n참조 영상 생성:\nVidu Q3: 플래시(1080p) 1s-16s, 초당 3크레딧; 시네마틱(1080p) 1s-16s, 초당 6크레딧.\nVidu Q2 Pro: 1080p, 2s-5s 20크레딧, 6s-8s 30크레딧.\nVidu Q2: 1080p, 2s-5s 12크레딧, 6s-8s 20크레딧.\nVidu Q1: 1080p, 5s 20크레딧.\nVidu 2.0: 고속 4s 4크레딧; 720p 4s 12크레딧.\n이미지 영상 생성:\nVidu Q3: 플래시(1080p) 1s-5s 10크레딧, 6s-10s 30크레딧, 11s-14s 50크레딧, 15s-16s 70크레딧; 시네마틱(1080p) 1s-5s 50크레딧, 6s-10s 90크레딧, 11s-14s 130크레딧, 15s-16s 150크레딧.\nVidu Q2: 플래시(1080p) 2s-5s 10크레딧, 6s-8s 20크레딧; 시네마틱(1080p) 2s-5s 35크레딧, 6s-8s 70크레딧.\nVidu Q1: 1080p, 5s 20크레딧.\nVidu 2.0: 고속 4s 4크레딧; 720p 4s 12크레딧, 8s 24크레딧; 1080p 4s 24크레딧.\n텍스트 영상 생성:\nVidu Q3: 플래시(1080p) 1s-5s 10크레딧, 6s-10s 25크레딧, 11s-14s 40크레딧, 15s-16s 60크레딧; 시네마틱(1080p) 1s-5s 40크레딧, 6s-10s 80크레딧, 11s-14s 120크레딧, 15s-16s 140크레딧.\nVidu Q2: 1080p, 2s-5s 8크레딧, 6s-8s 15크레딧.\nVidu Q1: 1080p, 5s 20크레딧.\n한 번에 4개의 동영상을 생성하면 총 크레딧 = 1회 크레딧 × 4입니다.',
          },
          {
            question: '사용하지 않은 구독 크레딧은 다음 구독 주기로 이월되나요?',
            answer:
              '아니요. 사용하지 않은 구독 크레딧은 다음 주기로 이월되지 않습니다. 현재 구독 유효 기간 내에 사용하시길 권장드립니다.',
          },
          {
            question: '크레딧이 부족할 경우 어떻게 해야 하나요?',
            answer:
              '사용 목적에 더 적합한 구독 플랜을 선택하시길 권장드립니다. 그래도 크레딧이 부족하다면 추가 크레딧을 구매하실 수 있습니다. 로그인 후 페이지 우측 상단의 "플랜 상세"로 들어가 "크레딧 구매"를 클릭하면 원하는 크레딧 패키지를 선택해 결제하실 수 있습니다.',
          },
          {
            question: '동영상 생성에 실패할 경우 크레딧을 환불받을 수 있나요?',
            answer:
              '동영상 생성 실패에는 보통 두 가지 경우가 있습니다:\n1. 서버 부하 증가, 네트워크 문제, 시스템 점검 또는 업데이트로 인한 생성 실패.\n2. 위반 콘텐츠로 인해 콘텐츠 심사를 통과하지 못한 경우.\n첫 번째 경우라면 시스템이 해당 작업에 사용된 크레딧을 자동으로 환불해 드립니다.',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: '요금제 구독',
        children: [
          {
            question: '구독 요금제마다 어떤 차이가 있나요?',
            answer:
              '각 요금제마다 제공되는 크레딧, 작업 우선 순위, 추가 기능 접근 권한에서 차이가 있습니다. 웹사이트의 "구독 요금제" 섹션을 방문해 자세한 사항을 확인해 보세요.',
          },
          {
            question: '1달 동안만 구독할 수 있나요?',
            answer:
              '현재 구독 서비스는 월간 및 연간 구독만 제공되고 있습니다. 1달 동안만 구독하고 싶으시다면 다음 결제일 전에 언제든지 취소하실 수 있습니다.',
          },
          {
            question: '다른 사람과 구독을 공유할 수 있나요?',
            answer:
              '구독 서비스는 개인 계정에 연결되어 있으며 공유할 수 없습니다. 각 사용자마다 개별 요금제를 사용해야 합니다.',
          },
          {
            question: '구독 요금제를 변경하려면 어떻게 해야 하나요?',
            answer:
              '죄송하지만, 현재 구독 중인 요금제를 직접 변경하기는 어렵습니다. 하지만 현재 이용 중인 요금제를 취소하고 원하는 요금제에 새로 가입하실 수 있으며, 이 경우 새 요금제는 다음 구독 주기부터 적용됩니다. 요금제 직접 변경 기능은 추후 업데이트될 예정입니다.',
          },
          {
            question: '구독 시 자동으로 요금이 청구되나요? 자동 갱신을 취소하려면 어떻게 해야 하나요?',
            answer:
              '네, 각 구독 주기가 시작될 때마다 자동으로 요금이 청구됩니다. 자동 갱신을 취소하려면 계정 설정으로 이동해 현재 이용 중인 구독 요금제를 취소하시면 됩니다.',
          },
        ],
      },
      {
        id: 'question_payments',
        question: '결제 및 지불',
        children: [
          {
            question: 'Vidu에서 사용할 수 있는 결제 수단에는 어떤 것들이 있나요?',
            answer:
              '현재 결제 시스템 Stripe를 통한 결제만 지원하고 있으며, 신용 카드 및 체크 카드(Visa, MasterCard, UnionPay 등) 사용이 가능합니다. 추후 업데이트를 통해 더 많은 결제 방법을 지원할 예정입니다.',
          },
          {
            question: '거래 명세서는 어떻게 신청하나요?',
            answer:
              '결제 시 여러분의 이메일 주소로 거래 명세서가 자동 발송됩니다. 찾을 수 없다면 스팸 메일함을 확인해 주세요.',
          },
          {
            question: '결제에 실패했습니다. 이유를 알 수 있나요?',
            answer:
              '결제 수단 오류, 잔액 부족 혹은 기술적 문제로 인해 결제에 실패할 수 있습니다. 결제 정보가 최신 상태인지 확인하시고 다시 시도해 주세요. 문제가 지속될 경우 support@vidu.com으로 문의하여 추가 지원을 요청해 주세요.',
          },
          {
            question: '환불 요청이 가능한가요?',
            answer:
              '죄송하지만, 현재는 환불 기능이 지원되지 않습니다. 다음 구독 주기가 시작되기 전에 요금제를 취소하시면 추가 결제가 진행되지 않도록 할 수 있습니다.',
          },
          {
            question: '결제 수단을 변경하려면 어떻게 해야 하나요?',
            answer:
              '결제 수단을 변경하시려면 프로필을 클릭한 뒤 드롭다운 메뉴에서 "구독" 페이지로 이동해 "결제 수단 변경"을 클릭하면 Stripe로 이동해 변경이 가능합니다.',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    pt: [
      {
        id: 'question_credits',
        question: 'Créditos',
        children: [
          {
            question: 'O que são créditos?',
            answer:
              'Os créditos são usados para gerar vídeos e existem três tipos:\n1. Créditos de assinatura: a cota de créditos incluída no seu plano, válidos por 30 dias.\n2. Créditos comprados: créditos adicionais que você pode comprar conforme a necessidade, válidos por 2 anos.\n3. Créditos bônus: créditos extras obtidos por meio de eventos, competições, do Programa de Artistas e atividades semelhantes, válidos por 2 anos.',
          },
          {
            question: 'Como obter créditos?',
            answer:
              '1. Bônus de cadastro: novos usuários recebem créditos de teste ao se registrarem no Vidu.\n2. Assinatura: assine um plano para receber mais créditos.\n3. Login diário: ganhe créditos fazendo login todos os dias.\n4. Eventos: participe de eventos oficiais, competições ou do Programa de Artistas para ganhar créditos bônus. Confira a seção de Eventos do Vidu para mais detalhes.',
          },
          {
            question: 'Como é determinado o consumo de créditos de cada tarefa?',
            answer:
              'A seguir está o consumo de créditos para um único vídeo.\nReferência para vídeo:\nVidu Q3: Relâmpago (1080p) 1s-16s, 3 créditos/s; Cinemático (1080p) 1s-16s, 6 créditos/s.\nVidu Q2 Pro: 1080p, 2s-5s custa 20 créditos, 6s-8s custa 30 créditos.\nVidu Q2: 1080p, 2s-5s custa 12 créditos, 6s-8s custa 20 créditos.\nVidu Q1: 1080p, 5s custa 20 créditos.\nVidu 2.0: Velocidade 4s custa 4 créditos; 720p 4s custa 12 créditos.\nImagem para vídeo:\nVidu Q3: Relâmpago (1080p) 1s-5s custa 10 créditos, 6s-10s custa 30 créditos, 11s-14s custa 50 créditos, 15s-16s custa 70 créditos; Cinemático (1080p) 1s-5s custa 50 créditos, 6s-10s custa 90 créditos, 11s-14s custa 130 créditos, 15s-16s custa 150 créditos.\nVidu Q2: Relâmpago (1080p) 2s-5s custa 10 créditos, 6s-8s custa 20 créditos; Cinemático (1080p) 2s-5s custa 35 créditos, 6s-8s custa 70 créditos.\nVidu Q1: 1080p, 5s custa 20 créditos.\nVidu 2.0: Velocidade 4s custa 4 créditos; 720p 4s custa 12 créditos, 8s custa 24 créditos; 1080p 4s custa 24 créditos.\nTexto para vídeo:\nVidu Q3: Relâmpago (1080p) 1s-5s custa 10 créditos, 6s-10s custa 25 créditos, 11s-14s custa 40 créditos, 15s-16s custa 60 créditos; Cinemático (1080p) 1s-5s custa 40 créditos, 6s-10s custa 80 créditos, 11s-14s custa 120 créditos, 15s-16s custa 140 créditos.\nVidu Q2: 1080p, 2s-5s custa 8 créditos, 6s-8s custa 15 créditos.\nVidu Q1: 1080p, 5s custa 20 créditos.\nSe você gerar 4 vídeos de uma vez, o total de créditos = créditos de uma geração × 4.',
          },
          {
            question: 'Os créditos de assinatura não utilizados passam para o próximo ciclo de assinatura?',
            answer:
              'Não. Os créditos de assinatura não utilizados não passam para o próximo ciclo. Recomendamos usá-los dentro do seu período de assinatura atual.',
          },
          {
            question: 'E se eu ficar sem créditos?',
            answer:
              'Recomendamos escolher um plano de assinatura mais adequado às suas necessidades. Se ainda precisar de mais créditos, você pode comprar créditos adicionais. Após fazer login, abra "Detalhes do plano" no canto superior direito da página e clique em "Comprar créditos" para selecionar um pacote de créditos e concluir o pagamento.',
          },
          {
            question: 'Os créditos serão reembolsados se meu vídeo não for gerado?',
            answer:
              'Geralmente há dois motivos para a falha na geração de um vídeo:\n1. A geração falhou devido à alta carga do servidor, problemas de rede ou manutenção ou atualização do sistema.\n2. O vídeo não passou na moderação de conteúdo por causa de uma violação.\nNo primeiro caso, o sistema reembolsará automaticamente os créditos usados nessa tarefa.',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: 'Planos de Assinatura',
        children: [
          {
            question: 'Quais são as diferenças entre os diferentes planos de assinatura?',
            answer:
              'Cada plano de assinatura oferece diferentes quantidades de créditos, diferentes prioridades de tarefas e diferentes acessos a funcionalidades adicionais. Acesse “Planos de Assinatura” em nosso site para uma comparação detalhada dos planos.',
          },
          {
            question: 'Posso assinar por apenas um mês?',
            answer:
              'Atualmente oferecemos somente assinaturas mensais e anuais. Caso deseja assinar por apenas um mês, pode cancelar a qualquer momento antes do próximo ciclo de cobrança.',
          },
          {
            question: 'Posso compartilhar minha assinatura com outras pessoas?',
            answer:
              'Atualmente, as assinaturas são vinculadas a contas individuais e não podem ser compartilhadas. Cada usuário deve ter seu próprio plano.',
          },
          {
            question: 'Como mudar meu plano de assinatura?',
            answer:
              'Infelizmente, não oferecemos alterações para planos de assinatura. Porém, você pode cancelar o seu plano atual e se inscrever novamente no plano desejado. O novo plano entrará em vigor no próximo ciclo de assinatura. Alterações diretas estarão disponíveis em breve.',
          },
          {
            question: 'Serei cobrado automaticamente? Como cancelar a renovação automática?',
            answer:
              'Sim, você será cobrado automaticamente no início de cada ciclo de cobrança. Para cancelar a renovação automática, vá para as configurações da sua conta e cancele seu plano de assinatura atual.',
          },
        ],
      },
      {
        id: 'question_payments',
        question: 'Pagamentos',
        children: [
          {
            question: 'Quais métodos de pagamento o Vidu aceita?',
            answer:
              'Atualmente, aceitamos apenas pagamentos por meio de nosso parceiro Stripe. Você pode usar cartões de crédito ou débito (Visa, MasterCard, UnionPay, etc.) para concluir os pagamentos. Ofereceremos mais opções de pagamento no futuro.',
          },
          {
            question: 'Como solicitar uma fatura?',
            answer:
              'Uma fatura será enviada automaticamente para o endereço de e-mail fornecido no momento do pagamento. Se não tiver recebido, verifique sua pasta de spam ou lixeira.',
          },
          {
            question: 'Por que o pagamento falhou?',
            answer:
              'Falhas no pagamento podem acontecer por saldo insuficiente ou problemas técnicos do seu método de pagamento. Certifique-se de que os detalhes do pagamento estejam atualizados e tente novamente. Se o problema persistir, entre em contato com support@vidu.com para mais assistência.',
          },
          {
            question: 'Posso solicitar um reembolso?',
            answer:
              'Lamentamos, mas os reembolsos não são suportados no momento. Você pode cancelar seu plano de assinatura antes do próximo ciclo de cobrança para evitar futuras cobranças.',
          },
          {
            question: 'Como alterar meu método de pagamento?',
            answer:
              'Para modificar seu método de pagamento, clique no avatar do seu perfil, acesse a página “Assinatura” no menu suspenso e clique em “Modificar Método de Pagamento” para ser redirecionado ao Stripe para alterações.',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    de: [
      {
        id: 'question_credits',
        question: 'Credits',
        children: [
          {
            question: 'Was sind Credits?',
            answer:
              'Credits werden zur Videogenerierung verwendet und es gibt drei Typen:\n1. Abo-Credits: das in deinem Plan enthaltene Credit-Kontingent, gültig für 30 Tage.\n2. Gekaufte Credits: zusätzliche Credits, die du nach Bedarf kaufen kannst, gültig für 2 Jahre.\n3. Bonus-Credits: zusätzliche Credit-Prämien aus Events, Wettbewerben, dem Künstlerprogramm und ähnlichen Aktivitäten, gültig für 2 Jahre.',
          },
          {
            question: 'Wie erhalte ich Credits?',
            answer:
              '1. Registrierungsbonus: Neue Nutzer erhalten nach der Anmeldung bei Vidu Test-Credits.\n2. Abonnement: Abonniere einen Plan, um mehr Credits zu erhalten.\n3. Tägliches Einloggen: Erhalte Credits durch tägliches Einloggen.\n4. Events: Nimm an offiziellen Events, Wettbewerben oder dem Künstlerprogramm teil, um Bonus-Credits zu erhalten. Details findest du im Vidu-Eventbereich.',
          },
          {
            question: 'Wie wird der Credit-Verbrauch der einzelnen Aufgaben bestimmt?',
            answer:
              'Nachfolgend der Credit-Verbrauch für ein einzelnes Video.\nReferenz-zu-Video:\nVidu Q3: Flash (1080p) 1s-16s, 3 Credits/s; Cinematic (1080p) 1s-16s, 6 Credits/s.\nVidu Q2 Pro: 1080p, 2s-5s kostet 20 Credits, 6s-8s kostet 30 Credits.\nVidu Q2: 1080p, 2s-5s kostet 12 Credits, 6s-8s kostet 20 Credits.\nVidu Q1: 1080p, 5s kostet 20 Credits.\nVidu 2.0: Geschwindigkeit 4s kostet 4 Credits; 720p 4s kostet 12 Credits.\nBild-zu-Video:\nVidu Q3: Flash (1080p) 1s-5s kostet 10 Credits, 6s-10s kostet 30 Credits, 11s-14s kostet 50 Credits, 15s-16s kostet 70 Credits; Cinematic (1080p) 1s-5s kostet 50 Credits, 6s-10s kostet 90 Credits, 11s-14s kostet 130 Credits, 15s-16s kostet 150 Credits.\nVidu Q2: Flash (1080p) 2s-5s kostet 10 Credits, 6s-8s kostet 20 Credits; Cinematic (1080p) 2s-5s kostet 35 Credits, 6s-8s kostet 70 Credits.\nVidu Q1: 1080p, 5s kostet 20 Credits.\nVidu 2.0: Geschwindigkeit 4s kostet 4 Credits; 720p 4s kostet 12 Credits, 8s kostet 24 Credits; 1080p 4s kostet 24 Credits.\nText-zu-Video:\nVidu Q3: Flash (1080p) 1s-5s kostet 10 Credits, 6s-10s kostet 25 Credits, 11s-14s kostet 40 Credits, 15s-16s kostet 60 Credits; Cinematic (1080p) 1s-5s kostet 40 Credits, 6s-10s kostet 80 Credits, 11s-14s kostet 120 Credits, 15s-16s kostet 140 Credits.\nVidu Q2: 1080p, 2s-5s kostet 8 Credits, 6s-8s kostet 15 Credits.\nVidu Q1: 1080p, 5s kostet 20 Credits.\nWenn du 4 Videos auf einmal generierst, gilt: Gesamt-Credits = Credits pro Generierung × 4.',
          },
          {
            question: 'Werden ungenutzte Abo-Credits in den nächsten Abo-Zeitraum übertragen?',
            answer:
              'Nein. Ungenutzte Abo-Credits werden nicht in den nächsten Zeitraum übertragen. Wir empfehlen, sie innerhalb deines aktuellen Abo-Zeitraums zu nutzen.',
          },
          {
            question: 'Was passiert, wenn mir die Credits ausgehen?',
            answer:
              'Wir empfehlen, einen Abo-Plan zu wählen, der besser zu deinen Anforderungen passt. Wenn du dennoch mehr Credits benötigst, kannst du zusätzliche Credits kaufen. Öffne nach dem Einloggen oben rechts auf der Seite „Plandetails“ und klicke auf „Credits kaufen“, um ein Credit-Paket auszuwählen und die Zahlung abzuschließen.',
          },
          {
            question: 'Werden Credits erstattet, wenn mein Video nicht generiert wird?',
            answer:
              'Für das Fehlschlagen einer Videogenerierung gibt es in der Regel zwei Gründe:\n1. Die Generierung ist aufgrund hoher Serverauslastung, Netzwerkproblemen oder Systemwartung bzw. -updates fehlgeschlagen.\n2. Das Video hat die Inhaltsprüfung wegen eines Verstoßes nicht bestanden.\nIm ersten Fall erstattet das System automatisch die für diese Aufgabe verwendeten Credits.',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: 'Abonnementpläne',
        children: [
          {
            question: 'Worin unterscheiden sich die verschiedenen Abo-Pläne?',
            answer:
              'Jeder Abo-Plan umfasst eine unterschiedliche Anzahl an Credits, unterschiedliche Prioritäten bei Aufgaben sowie verschiedenartige Zusatzfunktionen. Eine detaillierte Übersicht findest du im Bereich „Abonnementpläne“ auf unserer Website.',
          },
          {
            question: 'Kann ich nur für einen Monat abonnieren?',
            answer:
              'Derzeit bieten wir nur Monats- und Jahresabos an. Wenn du nur einen Monat nutzen möchtest, kannst du jederzeit vor Beginn des nächsten Abo-Zeitraums kündigen.',
          },
          {
            question: 'Kann ich mein Abo mit anderen teilen?',
            answer:
              'Momentan sind Abos an einzelne Konten gebunden und können nicht geteilt werden. Jeder Nutzer benötigt seinen eigenen Plan.',
          },
          {
            question: 'Wie kann ich meinen Abo-Plan ändern?',
            answer:
              'Leider unterstützen wir derzeit keine direkten Änderungen am Abo-Plan. Du kannst jedoch dein aktuelles Abo kündigen und anschließend den gewünschten Plan neu abschließen. Der neue Plan gilt dann ab dem nächsten Abo-Zeitraum. Direkte Planwechsel werden bald verfügbar sein.',
          },
          {
            question: 'Wird mein Abo automatisch verlängert? Wie kündige ich die automatische Verlängerung?',
            answer:
              'Ja, dein Abo wird zu Beginn jedes Abo-Zeitraums automatisch verlängert und abgerechnet. Um die automatische Verlängerung zu stoppen, gehe in deine Kontoeinstellungen und kündige dein aktuelles Abo.',
          },
        ],
      },
      {
        id: 'question_payments',
        question: 'Zahlungen',
        children: [
          {
            question: 'Welche Zahlungsmethoden akzeptiert Vidu?',
            answer:
              'Derzeit unterstützen wir Zahlungen ausschließlich über unseren Partner Stripe. Du kannst Kredit- oder Debitkarten (Visa, MasterCard, UnionPay usw.) verwenden. Weitere Zahlungsmethoden werden wir in Zukunft anbieten.',
          },
          {
            question: 'Wie erhalte ich eine Rechnung?',
            answer:
              'Eine Rechnung wird automatisch an die beim Bezahlen angegebene E-Mail-Adresse gesendet. Falls du sie nicht erhältst, überprüfe bitte deinen Spam- oder Junk-Ordner.',
          },
          {
            question: 'Warum ist meine Zahlung fehlgeschlagen?',
            answer:
              'Zahlungen können fehlschlagen, wenn es Probleme mit der Zahlungsmethode gibt, das Guthaben nicht ausreicht oder technische Fehler auftreten. Bitte stelle sicher, dass deine Zahlungsdaten aktuell sind, und versuche es erneut. Sollte das Problem weiterhin bestehen, wende dich bitte an support@vidu.com.',
          },
          {
            question: 'Kann ich eine Rückerstattung beantragen?',
            answer:
              'Leider unterstützen wir derzeit keine Rückerstattungen. Du kannst jedoch dein Abo vor Beginn des nächsten Abo-Zeitraums kündigen, um künftige Zahlungen zu vermeiden.',
          },
          {
            question: 'Wie ändere ich meine Zahlungsmethode?',
            answer:
              'Um deine Zahlungsmethode zu ändern, klicke auf dein Profilbild, wähle im Menü „Abonnieren“ und klicke auf „Zahlungsmethode ändern“. Du wirst zu Stripe weitergeleitet, um die Anpassungen vorzunehmen.',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    fr: [
      {
        id: 'question_credits',
        question: 'Crédits',
        children: [
          {
            question: "Qu'est-ce que les crédits ?",
            answer:
              "Les crédits servent à générer des vidéos et se déclinent en trois types :\n1. Crédits d'abonnement : le quota de crédits inclus dans votre forfait, valable 30 jours.\n2. Crédits achetés : crédits supplémentaires que vous pouvez acheter selon vos besoins, valables 2 ans.\n3. Crédits bonus : crédits supplémentaires obtenus via des événements, concours, le Programme Artiste et activités similaires, valables 2 ans.",
          },
          {
            question: 'Comment obtenir des crédits ?',
            answer:
              "1. Bonus d'inscription : les nouveaux utilisateurs reçoivent des crédits d'essai après leur inscription sur Vidu.\n2. Abonnement : abonnez-vous à un forfait pour recevoir plus de crédits.\n3. Connexion quotidienne : gagnez des crédits en vous connectant chaque jour.\n4. Événements : participez aux événements officiels, concours ou au Programme Artiste pour obtenir des crédits bonus. Consultez la section Événements de Vidu pour plus de détails.",
          },
          {
            question: 'Comment est déterminée la consommation de crédits de chaque tâche ?',
            answer:
              "Voici la consommation de crédits pour une seule vidéo.\nRéférence vers vidéo :\nVidu Q3 : Flash (1080p) 1s-16s, 3 crédits/s ; Cinématique (1080p) 1s-16s, 6 crédits/s.\nVidu Q2 Pro : 1080p, 2s-5s coûte 20 crédits, 6s-8s coûte 30 crédits.\nVidu Q2 : 1080p, 2s-5s coûte 12 crédits, 6s-8s coûte 20 crédits.\nVidu Q1 : 1080p, 5s coûte 20 crédits.\nVidu 2.0 : Vitesse 4s coûte 4 crédits ; 720p 4s coûte 12 crédits.\nImage vers vidéo :\nVidu Q3 : Flash (1080p) 1s-5s coûte 10 crédits, 6s-10s coûte 30 crédits, 11s-14s coûte 50 crédits, 15s-16s coûte 70 crédits ; Cinématique (1080p) 1s-5s coûte 50 crédits, 6s-10s coûte 90 crédits, 11s-14s coûte 130 crédits, 15s-16s coûte 150 crédits.\nVidu Q2 : Flash (1080p) 2s-5s coûte 10 crédits, 6s-8s coûte 20 crédits ; Cinématique (1080p) 2s-5s coûte 35 crédits, 6s-8s coûte 70 crédits.\nVidu Q1 : 1080p, 5s coûte 20 crédits.\nVidu 2.0 : Vitesse 4s coûte 4 crédits ; 720p 4s coûte 12 crédits, 8s coûte 24 crédits ; 1080p 4s coûte 24 crédits.\nTexte vers vidéo :\nVidu Q3 : Flash (1080p) 1s-5s coûte 10 crédits, 6s-10s coûte 25 crédits, 11s-14s coûte 40 crédits, 15s-16s coûte 60 crédits ; Cinématique (1080p) 1s-5s coûte 40 crédits, 6s-10s coûte 80 crédits, 11s-14s coûte 120 crédits, 15s-16s coûte 140 crédits.\nVidu Q2 : 1080p, 2s-5s coûte 8 crédits, 6s-8s coûte 15 crédits.\nVidu Q1 : 1080p, 5s coûte 20 crédits.\nSi vous générez 4 vidéos à la fois, total des crédits = crédits d'une génération × 4.",
          },
          {
            question: "Les crédits d'abonnement non utilisés sont-ils reportés sur le cycle d'abonnement suivant ?",
            answer:
              "Non. Les crédits d'abonnement non utilisés ne sont pas reportés sur le cycle suivant. Nous vous recommandons de les utiliser pendant votre période d'abonnement en cours.",
          },
          {
            question: "Que faire si je n'ai plus de crédits ?",
            answer:
              "Nous vous recommandons de choisir un forfait d'abonnement mieux adapté à vos besoins. Si vous avez encore besoin de crédits, vous pouvez en acheter davantage. Après connexion, ouvrez « Détails du forfait » en haut à droite de la page et cliquez sur « Acheter des crédits » pour sélectionner un pack de crédits et finaliser le paiement.",
          },
          {
            question: 'Mes crédits sont-ils remboursés si la génération de ma vidéo échoue ?',
            answer:
              "Un échec de génération de vidéo a généralement deux causes :\n1. La génération a échoué en raison d'une forte charge serveur, de problèmes réseau ou d'une maintenance ou mise à jour du système.\n2. La vidéo n'a pas passé la modération de contenu en raison d'une infraction.\nDans le premier cas, le système rembourse automatiquement les crédits utilisés pour cette tâche.",
          },
        ],
      },
      {
        id: 'question_subscription',
        question: "Plans d'abonnement",
        children: [
          {
            question: "Quelles sont les différences entre les différents plans d'abonnement ?",
            answer:
              "Chaque plan propose un nombre de crédits différent, des priorités de tâches différentes et un accès variable à certaines fonctionnalités supplémentaires. Consultez la section « Plans d'abonnement » de notre site web pour une comparaison détaillée.",
          },
          {
            question: "Puis-je m'abonner pour un seul mois ?",
            answer:
              "Nous proposons actuellement uniquement des abonnements mensuels et annuels. Si vous souhaitez n'utiliser le service qu'un mois, vous pouvez annuler votre abonnement à tout moment avant le prochain cycle de facturation.",
          },
          {
            question: "Puis-je partager mon abonnement avec d'autres personnes ?",
            answer:
              'Non. Les abonnements sont actuellement liés à des comptes individuels et ne peuvent pas être partagés. Chaque utilisateur doit disposer de son propre plan.',
          },
          {
            question: "Comment changer mon plan d'abonnement ?",
            answer:
              "Nous ne prenons malheureusement pas encore en charge la modification directe d'un plan. Cependant, vous pouvez annuler votre abonnement actuel, puis vous réabonner au plan de votre choix. Le nouveau plan prendra effet au prochain cycle d'abonnement. Les changements directs de plan seront disponibles prochainement.",
          },
          {
            question: 'Serai-je facturé automatiquement ? Comment annuler le renouvellement automatique ?',
            answer:
              "Oui, vous serez facturé automatiquement au début de chaque cycle de facturation. Pour annuler le renouvellement automatique, accédez aux paramètres de votre compte et annulez votre plan d'abonnement actuel.",
          },
        ],
      },
      {
        id: 'question_payments',
        question: 'Paiements',
        children: [
          {
            question: 'Quels moyens de paiement Vidu accepte-t-il ?',
            answer:
              "Nous prenons actuellement en charge uniquement les paiements via notre partenaire Stripe. Vous pouvez utiliser des cartes de crédit ou de débit (Visa, MasterCard, UnionPay, etc.) pour effectuer vos paiements. D'autres options de paiement seront disponibles prochainement.",
          },
          {
            question: 'Comment demander une facture ?',
            answer:
              "Une facture sera automatiquement envoyée à l'adresse e-mail fournie au moment du paiement. Si vous ne la recevez pas, veuillez vérifier vos dossiers courrier indésirable / spam.",
          },
          {
            question: 'Pourquoi mon paiement a-t-il échoué ?',
            answer:
              "Les échecs de paiement peuvent survenir pour différentes raisons : problème avec votre moyen de paiement, fonds insuffisants, problème technique. Veuillez vérifier que vos informations de paiement sont à jour, puis réessayez. Si le problème persiste, veuillez contacter support@vidu.com pour obtenir de l'aide.",
          },
          {
            question: 'Puis-je demander un remboursement ?',
            answer:
              "Nous sommes désolés, mais les remboursements ne sont pas pris en charge pour le moment. Vous pouvez toutefois annuler votre plan d'abonnement avant le prochain cycle de facturation afin d'éviter toute facturation future.",
          },
          {
            question: 'Comment changer mon moyen de paiement ?',
            answer:
              "Pour modifier votre moyen de paiement, cliquez sur votre avatar de profil, accédez à la page « S'abonner » depuis le menu déroulant, puis cliquez sur « Modifier le mode de paiement ». Vous serez alors redirigé vers Stripe pour effectuer les changements nécessaires.",
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
    ru: [
      {
        id: 'question_credits',
        question: 'Кредиты',
        children: [
          {
            question: 'Что такое кредиты?',
            answer:
              'Кредиты используются для создания видео и бывают трёх типов:\n1. Кредиты по подписке: лимит кредитов, включённый в ваш тариф, действует 30 дней.\n2. Купленные кредиты: дополнительные кредиты, которые можно приобрести по мере необходимости, действуют 2 года.\n3. Бонусные кредиты: дополнительное вознаграждение в кредитах за участие в мероприятиях, конкурсах, Программе для художников и подобных активностях, действуют 2 года.',
          },
          {
            question: 'Как получить кредиты?',
            answer:
              '1. Бонус за регистрацию: новые пользователи получают пробные кредиты после регистрации в Vidu.\n2. Подписка: оформите подписку на тариф, чтобы получить больше кредитов.\n3. Ежедневный вход: получайте кредиты, заходя в систему каждый день.\n4. Мероприятия: участвуйте в официальных мероприятиях, конкурсах или Программе для художников, чтобы получить бонусные кредиты. Подробности — в разделе мероприятий Vidu.',
          },
          {
            question: 'Как определяется расход кредитов для разных задач?',
            answer:
              'Ниже указан расход кредитов на одно видео.\nГенерация видео по референсу:\nVidu Q3: Молниеносный (1080p) 1s-16s, 3 кредита/с; Кинематографический (1080p) 1s-16s, 6 кредитов/с.\nVidu Q2 Pro: 1080p, 2s-5s — 20 кредитов, 6s-8s — 30 кредитов.\nVidu Q2: 1080p, 2s-5s — 12 кредитов, 6s-8s — 20 кредитов.\nVidu Q1: 1080p, 5s — 20 кредитов.\nVidu 2.0: Скорость 4s — 4 кредита; 720p 4s — 12 кредитов.\nГенерация видео из изображения:\nVidu Q3: Молниеносный (1080p) 1s-5s — 10 кредитов, 6s-10s — 30 кредитов, 11s-14s — 50 кредитов, 15s-16s — 70 кредитов; Кинематографический (1080p) 1s-5s — 50 кредитов, 6s-10s — 90 кредитов, 11s-14s — 130 кредитов, 15s-16s — 150 кредитов.\nVidu Q2: Молниеносный (1080p) 2s-5s — 10 кредитов, 6s-8s — 20 кредитов; Кинематографический (1080p) 2s-5s — 35 кредитов, 6s-8s — 70 кредитов.\nVidu Q1: 1080p, 5s — 20 кредитов.\nVidu 2.0: Скорость 4s — 4 кредита; 720p 4s — 12 кредитов, 8s — 24 кредита; 1080p 4s — 24 кредита.\nГенерация видео из текста:\nVidu Q3: Молниеносный (1080p) 1s-5s — 10 кредитов, 6s-10s — 25 кредитов, 11s-14s — 40 кредитов, 15s-16s — 60 кредитов; Кинематографический (1080p) 1s-5s — 40 кредитов, 6s-10s — 80 кредитов, 11s-14s — 120 кредитов, 15s-16s — 140 кредитов.\nVidu Q2: 1080p, 2s-5s — 8 кредитов, 6s-8s — 15 кредитов.\nVidu Q1: 1080p, 5s — 20 кредитов.\nЕсли создать 4 видео за один раз, общий расход = кредиты за одну генерацию × 4.',
          },
          {
            question: 'Переносятся ли неиспользованные кредиты по подписке на следующий период?',
            answer:
              'Нет. Неиспользованные кредиты по подписке не переносятся на следующий период. Рекомендуем использовать их в течение текущего периода подписки.',
          },
          {
            question: 'Что делать, если кредиты закончились?',
            answer:
              'Рекомендуем выбрать тариф, который лучше подходит под ваши задачи. Если кредитов всё равно не хватает, можно докупить дополнительные. После входа откройте «Детали тарифа» в правом верхнем углу страницы и нажмите «Купить кредиты», чтобы выбрать пакет кредитов и завершить оплату.',
          },
          {
            question: 'Вернут ли кредиты при неудачной генерации видео?',
            answer:
              'Обычно генерация видео не удаётся по двум причинам:\n1. Сбой из-за высокой нагрузки на сервер, проблем с сетью либо технического обслуживания или обновления системы.\n2. Видео не прошло модерацию из-за нарушения правил.\nВ первом случае система автоматически вернёт кредиты, использованные для этой задачи.',
          },
        ],
      },
      {
        id: 'question_subscription',
        question: 'Тарифные планы',
        children: [
          {
            question: 'Чем отличаются тарифные планы?',
            answer:
              'Тарифы различаются количеством кредитов, приоритетом обработки и доступными функциями. Подробное сравнение доступно в разделе «Тарифные планы» на нашем сайте.',
          },
          {
            question: 'Можно ли оформить подписку только на месяц?',
            answer:
              'Доступны месячная и годовая подписки. Для использования только одного месяца можно отменить продление до начала следующего периода.',
          },
          {
            question: 'Можно ли использовать подписку совместно?',
            answer:
              'Подписка привязана к личному аккаунту и не подлежит совместному использованию. Каждому пользователю нужен отдельный тариф.',
          },
          {
            question: 'Как сменить тарифный план?',
            answer:
              'Прямая смена тарифа пока недоступна. Можно отменить текущий план и оформить новый — он активируется в следующем периоде. Функция прямой смены появится в ближайшее время.',
          },
          {
            question: 'Будет ли автоматическое списание? Как отключить автопродление?',
            answer:
              'Да, оплата происходит автоматически в начале каждого периода. Для отключения автопродления отмените подписку в настройках аккаунта.',
          },
        ],
      },
      {
        id: 'question_payments',
        question: 'Оплата',
        children: [
          {
            question: 'Какие способы оплаты доступны в Vidu?',
            answer:
              'Оплата принимается через платёжную систему Stripe. Доступны кредитные и дебетовые карты (Visa, MasterCard, UnionPay и другие). В будущем добавим новые способы оплаты.',
          },
          {
            question: 'Как получить счёт на оплату?',
            answer:
              'Счёт автоматически отправляется на email, указанный при оплате. Проверьте папку «Спам», если не получили письмо.',
          },
          {
            question: 'Почему платёж не прошёл?',
            answer:
              'Платёж может не пройти из-за проблем с картой, недостатка средств или технического сбоя. Проверьте платёжные данные и повторите попытку. При повторной ошибке обратитесь на support@vidu.com.',
          },
          {
            question: 'Можно ли вернуть деньги?',
            answer:
              'К сожалению, возврат средств временно недоступен. Отмените подписку до следующего периода, чтобы избежать списаний.',
          },
          {
            question: 'Как изменить способ оплаты?',
            answer:
              'Нажмите на аватар профиля, выберите «Подписка» в меню, затем «Изменить способ оплаты» — вас перенаправит в систему Stripe.',
          },
        ],
      },
      {
        id: 'question_clawPass',
        question: 'Video Plan',
        children: [],
      },
    ],
  },
  [
    {
      id: 'question_credits',
      question: '什么是积分？',
      children: [
        {
          question: '什么是积分？',
          answer:
            '积分可用于生成视频，分为三种类型：\n1. 订阅积分：套餐内包含的积分额度，有效期 30 天。\n2. 购买积分：可按需额外购买的积分，有效期 2 年。\n3. 奖励积分：通过活动、比赛、艺术家计划等方式获得的额外积分奖励，有效期 2 年。',
        },
        {
          question: '如何获取积分？',
          answer:
            '1. 注册赠送积分：新用户注册 Vidu 后，可获得体验积分。\n2. 订阅获取积分：可通过订阅服务获得更多积分。\n3. 每日登录获取积分：每日登录可获得积分。\n4. 参与活动获取积分：参与官方活动、比赛或艺术家计划，可获得额外积分奖励，详情可查看 Vidu 活动专区。',
        },
        {
          question: '如何确定不同任务的积分消耗明细？',
          answer:
            '以下为单个视频的积分消耗标准。\n参考生视频：\nVidu Q3：闪电出片（1080p）1s-16s，3 积分/s；电影大片（1080p）1s-16s，6 积分/s。\nVidu Q2 Pro：1080p，2s-5s 消耗 20 积分，6s-8s 消耗 30 积分。\nVidu Q2：1080p，2s-5s 消耗 12 积分，6s-8s 消耗 20 积分。\nVidu Q1：1080p，5s 消耗 20 积分。\nVidu 2.0：极速 4s 消耗 4 积分；720p 4s 消耗 12 积分。\n图生视频：\nVidu Q3：闪电出片（1080p）1s-5s 消耗 10 积分，6s-10s 消耗 30 积分，11s-14s 消耗 50 积分，15s-16s 消耗 70 积分；电影大片（1080p）1s-5s 消耗 50 积分，6s-10s 消耗 90 积分，11s-14s 消耗 130 积分，15s-16s 消耗 150 积分。\nVidu Q2：闪电出片（1080p）2s-5s 消耗 10 积分，6s-8s 消耗 20 积分；电影大片（1080p）2s-5s 消耗 35 积分，6s-8s 消耗 70 积分。\nVidu Q1：1080p，5s 消耗 20 积分。\nVidu 2.0：极速 4s 消耗 4 积分；720p 4s 消耗 12 积分、8s 消耗 24 积分；1080p 4s 消耗 24 积分。\n文生视频：\nVidu Q3：闪电出片（1080p）1s-5s 消耗 10 积分，6s-10s 消耗 25 积分，11s-14s 消耗 40 积分，15s-16s 消耗 60 积分；电影大片（1080p）1s-5s 消耗 40 积分，6s-10s 消耗 80 积分，11s-14s 消耗 120 积分，15s-16s 消耗 140 积分。\nVidu Q2：1080p，2s-5s 消耗 8 积分，6s-8s 消耗 15 积分。\nVidu Q1：1080p，5s 消耗 20 积分。\n如一次生成 4 个视频，总消耗积分 = 单次积分 × 4。',
        },
        {
          question: '未使用的订阅积分会转入下一个订阅周期吗？',
          answer: '不会。未使用的订阅积分不会转入下一个周期，建议您在当前订阅有效期内及时使用。',
        },
        {
          question: '如果我的积分用完了，该怎么办？',
          answer:
            '建议您选择更适合自己使用需求的订阅计划。如果积分仍然不足，也可以购买额外积分。登录后，在页面右上角进入“套餐详情”，点击“购买积分”即可选择对应积分套餐并完成支付。',
        },
        {
          question: '如果我的视频生成失败，是否会退还积分？',
          answer:
            '视频生成失败通常有两种情况：\n1. 由于服务器负载高、网络问题、系统维护或更新导致生成失败。\n2. 视频因违规内容审核不通过。\n如果属于第 1 种情况，系统将自动退还该任务消耗的积分。',
        },
      ],
    },
    {
      id: 'question_subscription',
      question: '订阅套餐',
      children: [
        {
          question: '不同订阅套餐之间有什么区别？',
          answer:
            '每个订阅套餐提供不同数量的积分、不同的任务优先级以及不同的附加功能访问权限。请访问我们网站上的 “套餐详情 ”部分，购买您喜爱的套餐。',
        },
        {
          question: '我可以只订阅一个月吗？',
          answer: '如果您只想订阅一个月，可以在官网右上角“套餐详情”下点击“按月购买”，即可订阅一个月的会员。',
        },
        {
          question: '我可以与他人分享我的订阅套餐吗？',
          answer: '目前，订阅套餐与个人账户绑定，不能共享。每个用户必须拥有自己的套餐。',
        },
        {
          question: '如何更改我的订阅套餐？',
          answer: '我们不支持直接更改订阅套餐。您可以等待当前套餐过期之后重新购买，或者注册一个新的账号购买。',
        },
        {
          question: '我会被自动收费吗？如何取消自动续费？',
          answer:
            '您可以随时通过支付渠道的自动续费设置取消或管理您的订阅。根据您购买时使用的支付渠道，取消流程可能有所不同。自动扣费会在到期前24小时扣款，且扣款后不支持退费。\n1.电脑端：Vidu会员自动续费服务，将在到期前自动为您发起续费，自动延长一个续费周期(月)。您可随时在支付宝-设置-支付设置-免密支付/自动续费，选择ViduAI订阅，点击取消订阅，取消后不再自动续费。（若您的免密支付里没有该续费服务，就代表已经取消了，下个月不会再续费）\n2.APP端:IOS-在每个续订日期前至少一天，在设置 › Apple 帐户中随时取消。计划会自动续订，直到取消为止。安卓-在支付宝-设置-支付设置-免密支付/自动续费，选择ViduAI订阅，点击取消订阅，取消后不再自动续费。',
        },
      ],
    },
    {
      id: 'question_payments',
      question: '付款问题',
      children: [
        {
          question: 'Vidu 接受哪些付款方式？',
          answer:
            '1.pc端：目前只支持通过支付宝进行付费。\n2.APP端：苹果用户只能通过Apple ID进行付费，安卓用户只能通过支付宝进行付费。',
        },
        {
          question: '如何开发票？',
          answer:
            '电脑端www.vidu.cn登录之后，点击头像-我的套餐-点击昵称下面的<订单>-右侧开发票，填写邮箱账户，填写之后打开邮箱会收到链接——点击链接进行填写就可以啦。（苹果支付需要到苹果支付平台开票）',
        },
        {
          question: '为什么我的付款失败了？',
          answer:
            '付款失败可能是由于您的付款方式、资金不足或技术问题造成的。请确保您的付款信息是最新的，然后再试一次。',
        },
        {
          question: '我可以申请退款吗？',
          answer:
            '我们很遗憾无法提供退款，服务费支付完成后，不可进行转让或退款，除非因Vidu产品技术原因，导致您购买时被重复扣款或因Vidu产品技术原因导致您无法享有购买的服务时，您可提出退款申诉，经过生数核实后可为您办理退款，但您需要提供相应信息并配合生数处理（具体可参考《Vidu付费服务协议》4.1）。退款可联系官方微信号：Viduforhuman 或发送退款申请至邮箱：support@vidu.cn',
        },
      ],
    },
    {
      id: 'question_clawPass',
      question: '创作者计划',
      children: [],
    },
  ],
);
